/* De mediabibliotheek van de site (/crm/website/site/media, ADR 0024): de beelden en pdf's waar de
 * content naar verwijst. Dit bestand tekent alleen. Wat een bestand mag zijn, hoe het in de opslag
 * heet, wat er wordt geschreven en waar een bestand gebruikt wordt, staat in data/media.ts.
 *
 * Eén scherm: bovenaan uploaden, daaronder de bibliotheek met zoeken. Per bestand het beeld (een klik
 * opent het bestand zelf), de naam, het pad om te kopiëren, de beschrijving en waar het in de content
 * staat. De naam en de beschrijving zijn te wijzigen; het pad ligt vast. Er is geen knop om een bestand
 * te verwijderen. De site gebruikt de beschrijving bij een beeld op de plekken waar de pagina zelf alleen
 * een titel heeft (BESCHRIJVING_PLEKKEN in MediaKiezer.tsx), en de naam niet: na een gewijzigde
 * beschrijving vraagt de CRM de site om te verversen (ververs.ts, ADR 0031), na alleen een andere naam
 * of na uploaden niet. */

import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useMedia, useMediaVerwijzingen, useMediaWijzigen, useRechten } from './hooks';
import { adresVan, gebruikVan, isBeeld, naamVan, padInContent, wijziging, zoek, type Invoer, type MediaBestand, type Plek, type Verwijzing } from './data/media';
import { BESCHRIJVING_OP_DE_SITE, BESCHRIJVING_UITLEG, KopieerPad, Miniatuur, STAP, Uploaden } from './MediaKiezer';
import { meldOpslag } from './ververs';
import { Fout, Kop } from './WebsiteBeheer';

const TERUG = { naar: '/crm/website/site', label: 'Content op de site' };
const TITEL = 'Media';
/** Zoveel plekken staan er bij een bestand; van de rest alleen het aantal. */
const PLEKKEN = 3;

const datum = (tijd: string | null | undefined): string => {
  const d = tijd ? new Date(tijd) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Amsterdam' }) : '';
};

export function WebsiteMedia() {
  const { magBewerken } = useRechten();
  const { data, error, isLoading } = useMedia();

  if (isLoading) return <p className="text-sm text-muted-foreground">Laden…</p>;
  if (error || !data) {
    return (
      <div className="space-y-4">
        <Kop titel={TITEL} terug={TERUG} />
        <Fout tekst={error ? (error as Error).message : 'De media zijn niet te lezen.'} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Kop titel={TITEL} terug={TERUG} />
      <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg="media">
        De beelden en pdf&apos;s van de site. In de content staat een bestand als <code>/media/</code> met de bestandsnaam erachter: kopieer het pad hier, of
        kies het beeld in een formulier met de knop Kies uit media. Een nieuw bestand verandert niets op de site tot je het ergens gebruikt.
        Hier wordt niets verwijderd.
      </p>
      {!magBewerken && <Fout tekst="Je kunt de media bekijken, maar niets uploaden of wijzigen." />}
      {magBewerken && (
        <Card data-kaart="uploaden">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Uploaden</CardTitle>
            <p className="text-sm text-muted-foreground">
              Een bestand krijgt in de opslag een nette naam met de tijd ervoor. Er wordt nooit een bestaand bestand vervangen.
            </p>
          </CardHeader>
          <CardContent><Uploaden /></CardContent>
        </Card>
      )}
      <Bibliotheek bestanden={data} magBewerken={magBewerken} />
    </div>
  );
}

/* ── De bibliotheek ────────────────────────────────────────────────────────── */

function Bibliotheek({ bestanden, magBewerken }: { bestanden: MediaBestand[]; magBewerken: boolean }) {
  const [vraag, setVraag] = useState('');
  const [aantal, setAantal] = useState(STAP);
  // Eén bestand tegelijk open om te bewerken.
  const [open, setOpen] = useState<string | null>(null);
  const verwijzingen = useMediaVerwijzingen();
  const gevonden = useMemo(() => zoek(bestanden, vraag), [bestanden, vraag]);
  const telling = gevonden.length === bestanden.length
    ? (bestanden.length === 1 ? '1 bestand' : `${bestanden.length} bestanden`)
    : `${gevonden.length} van ${bestanden.length} bestanden`;

  return (
    <Card data-kaart="bibliotheek">
      <CardHeader className="space-y-3 pb-3">
        <CardTitle className="text-base">{telling}</CardTitle>
        <div className="max-w-md space-y-1.5">
          <Label htmlFor="media-zoek">Zoek in de media</Label>
          <Input id="media-zoek" type="search" value={vraag} placeholder="Zoek op naam of beschrijving" onChange={(e) => setVraag(e.target.value)} />
        </div>
        <p className="max-w-3xl text-xs text-muted-foreground" data-uitleg="gebruik">
          {verwijzingen.error
            ? 'Waar een bestand in de content staat, is nu niet te lezen.'
            : 'Bij elk bestand staat waar het in de content voorkomt. De code van de site kan een bestand ook gebruiken: dat ziet de CRM niet.'}
        </p>
        <p className="max-w-3xl text-xs text-muted-foreground" data-uitleg="beschrijving-op-de-site">{BESCHRIJVING_OP_DE_SITE}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {gevonden.length === 0 && (
          <p className="text-sm text-muted-foreground">{bestanden.length === 0 ? 'Er staan nog geen bestanden in de media.' : 'Geen bestand gevonden met deze zoekwoorden.'}</p>
        )}
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {gevonden.slice(0, aantal).map((bestand) => (
            <Tegel key={bestand.id} bestand={bestand} verwijzingen={verwijzingen.data ?? null} magBewerken={magBewerken}
              open={open === bestand.id} openen={() => setOpen(bestand.id)} sluiten={() => setOpen(null)} />
          ))}
        </ul>
        {gevonden.length > aantal && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => setAantal((a) => a + STAP)}>Toon er nog {Math.min(STAP, gevonden.length - aantal)}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Waar het bestand in de content staat: de eerste plekken met een link naar het formulier. */
function Gebruik({ plekken }: { plekken: Plek[] }) {
  if (plekken.length === 0) return <p className="text-xs text-muted-foreground" data-gebruik="0">Niet gevonden in de content.</p>;
  return (
    <p className="text-xs text-muted-foreground" data-gebruik={plekken.length}>
      In gebruik bij{' '}
      {plekken.slice(0, PLEKKEN).map((plek, i) => (
        <Fragment key={`${plek.wat}-${i}`}>
          {i > 0 && ', '}
          {plek.naar ? <Link to={plek.naar} className="underline hover:text-foreground">{plek.wat}</Link> : plek.wat}
        </Fragment>
      ))}
      {plekken.length > PLEKKEN && ` en nog ${plekken.length - PLEKKEN}`}.
    </p>
  );
}

function Tegel({ bestand, verwijzingen, magBewerken, open, openen, sluiten }: {
  bestand: MediaBestand; verwijzingen: Verwijzing[] | null; magBewerken: boolean; open: boolean; openen: () => void; sluiten: () => void;
}) {
  const pad = padInContent(bestand);
  const plekken = useMemo(() => (verwijzingen ? gebruikVan(bestand, verwijzingen) : null), [bestand, verwijzingen]);
  const beschrijving = bestand.alt_text?.trim();
  return (
    <li className="space-y-2 rounded-md border p-3" data-bestand={bestand.storage_path}>
      {/* Op een telefoon staat het beeld klein links van de tekst, op een breder scherm erboven. */}
      <div className="flex gap-3 sm:flex-col">
        {/* Een klik op het beeld opent het bestand zelf, in een nieuw tabblad. */}
        <a href={adresVan(bestand)} target="_blank" rel="noopener noreferrer" className="block w-24 shrink-0 sm:w-full" title="Open het bestand in een nieuw tabblad" aria-label={`Open ${naamVan(bestand)} in een nieuw tabblad`} data-open>
          <Miniatuur bestand={bestand} className="aspect-square w-full sm:aspect-[4/3]" />
        </a>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="break-words text-sm font-medium">{naamVan(bestand)}</div>
          <code className="block break-all text-xs text-muted-foreground" data-pad>{pad}</code>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{datum(bestand.created_at)}</span>
            {!isBeeld(bestand) && <Badge variant="secondary">pdf</Badge>}
            {(bestand.tags ?? []).map((label) => <Badge key={label} variant="outline">{label}</Badge>)}
          </div>
          {beschrijving && <p className="text-xs" data-beschrijving="ja">{beschrijving}</p>}
          {!beschrijving && isBeeld(bestand) && <p className="text-xs text-amber-600" data-beschrijving="nee">Nog geen beschrijving.</p>}
        </div>
      </div>
      {plekken && <Gebruik plekken={plekken} />}
      {!open && (
        <div className="flex flex-wrap gap-2">
          <KopieerPad pad={pad} />
          {magBewerken && <Button variant="ghost" size="sm" onClick={openen}><Pencil className="mr-1 h-4 w-4" /> Bewerken</Button>}
        </div>
      )}
      {open && <Bewerk bestand={bestand} sluiten={sluiten} />}
    </li>
  );
}

function Bewerk({ bestand, sluiten }: { bestand: MediaBestand; sluiten: () => void }) {
  const { toast } = useToast();
  const [concept, setConcept] = useState<Invoer>({ naam: bestand.display_name ?? '', beschrijving: bestand.alt_text ?? '' });
  const wijzigen = useMediaWijzigen();
  const uit = wijzigen.isPending;
  const gewijzigd = Object.keys(wijziging(bestand, concept)).length > 0;

  const bewaar = async () => {
    try {
      const uitkomst = await wijzigen.mutateAsync({ vorige: bestand, concept });
      sluiten();
      if (uitkomst.geschreven.length === 0) toast({ title: 'Niets gewijzigd', description: 'Er is niets opgeslagen.' });
      // De site gebruikt de naam niet: is alleen die gewijzigd, dan is er niets te verversen.
      else if (!uitkomst.verversing) toast({ title: 'Opgeslagen', description: 'De naam staat alleen in de bibliotheek: op de site verandert niets.' });
      // De beschrijving gebruikt de site wel. De melding staat er meteen; of de site al ververst is, komt er even later bij.
      else void meldOpslag(toast, uitkomst.verversing, 'Opgeslagen');
    } catch (mis) {
      toast({ title: 'Niet opgeslagen', description: mis instanceof Error ? mis.message : String(mis), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3" data-bewerk={bestand.storage_path}>
      <p className="text-xs text-muted-foreground">Het pad ligt vast: de content verwijst ernaar.</p>
      <div className="space-y-1.5">
        <Label htmlFor="media-naam">Naam</Label>
        <Input id="media-naam" value={concept.naam} disabled={uit} onChange={(e) => setConcept((c) => ({ ...c, naam: e.target.value }))} />
        <p className="text-xs text-muted-foreground">Waaronder je het bestand hier terugvindt. Zonder naam staat het pad er. De site gebruikt de naam niet.</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="media-beschrijving">Beschrijving</Label>
        <Textarea id="media-beschrijving" rows={2} value={concept.beschrijving} disabled={uit} placeholder="Wat staat er op het beeld?" onChange={(e) => setConcept((c) => ({ ...c, beschrijving: e.target.value }))} />
        <p className="text-xs text-muted-foreground">{BESCHRIJVING_UITLEG}</p>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" disabled={uit} onClick={sluiten}>Annuleren</Button>
        <Button onClick={bewaar} disabled={uit || !gewijzigd} size="sm">
          <Save className="mr-1 h-4 w-4" /> {uit ? 'Opslaan…' : 'Opslaan'}
        </Button>
      </div>
    </div>
  );
}
